import { Fragment, useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, Loader2, Lock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { QuantityStepper } from "@/components/ui/quantity-stepper";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { resolveProductPriceQuote, offerableUnits, offerableCutsForUnit } from "@/lib/catalogPricing";
import { deleteMyOrder, updateMyOrder } from "@/lib/ordersApi";
import { roundQuantity } from "@/lib/number";
import { ORDER_STATUS_LABELS } from "@/lib/orderUtils";
import { cn } from "@/lib/utils";
import { useApp } from "@/store/useApp";
import type { OrderDetail, OrderItem, Product } from "@/lib/types";

interface Props {
  order: OrderDetail | null;
  loading: boolean;
  onOpenChange: (open: boolean) => void;
  onOrderUpdated: (order: OrderDetail) => void;
  onOrderDeleted: (orderId: string) => void;
}

interface DraftOrderItem {
  externalId: string;
  productId: string;
  unit: string;
  quantity: number;
  cut: string;
  observation: string;
}

const DEFAULT_PERIOD = "Manhã (06h - 10h)";

function parseDeliveryWindow(value?: string) {
  if (!value) {
    return { date: "", period: DEFAULT_PERIOD };
  }

  const [datePart, periodPart] = value.split(" · ");
  const dateMatch = datePart?.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!dateMatch) {
    return { date: "", period: periodPart?.trim() || DEFAULT_PERIOD };
  }

  const [, day, month, year] = dateMatch;
  return {
    date: `${year}-${month}-${day}`,
    period: periodPart?.trim() || DEFAULT_PERIOD,
  };
}

function formatDeliveryWindow(date: string, period: string) {
  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year} · ${period}`;
}

function toDraftItem(item: OrderItem): DraftOrderItem {
  return {
    externalId: item.externalId,
    productId: item.productId,
    unit: item.unit,
    quantity: roundQuantity(item.quantity, 3),
    cut: item.cut ?? "",
    observation: item.observation ?? "",
  };
}

function findProduct(products: Product[], productId: string) {
  return products.find((product) => product.id === productId) ?? null;
}

function productUnits(product: Product | null) {
  if (!product) return [];
  return product.availableUnits?.length ? product.availableUnits : [product.defaultUnit];
}

function productCuts(product: Product | null) {
  if (!product) return [];
  return product.availableCuts ?? [];
}

function normalizeUnit(product: Product | null, unit: string) {
  const units = productUnits(product);
  if (unit && units.includes(unit)) return unit;
  if (product?.defaultUnit && units.includes(product.defaultUnit)) return product.defaultUnit;
  return units[0] ?? "";
}

function normalizeCut(product: Product | null, cut: string) {
  const cuts = productCuts(product);
  if (!cuts.length) return cut;
  if (cut && cuts.includes(cut)) return cut;
  return cuts[0] ?? "";
}

function formatMoney(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export function CustomerOrderDrawer({
  order,
  loading,
  onOpenChange,
  onOrderUpdated,
  onOrderDeleted,
}: Props) {
  const { catalogUnits, products, hydrateCatalog, catalogStatus } = useApp();
  const [deliveryDate, setDeliveryDate] = useState("");
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [notes, setNotes] = useState("");
  const [draftItems, setDraftItems] = useState<DraftOrderItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const activeProducts = useMemo(() => products.filter((product) => product.active !== false), [products]);
  const originalLineIds = useMemo(() => new Set(order?.items.map((item) => item.externalId) ?? []), [order]);

  useEffect(() => {
    if (!loading && !order) return;
    if (catalogStatus === "loading" || products.length > 0) return;
    void hydrateCatalog();
  }, [catalogStatus, hydrateCatalog, loading, order, products.length]);

  useEffect(() => {
    if (!order) return;
    const parsed = parseDeliveryWindow(order.deliveryWindow);
    setDeliveryDate(parsed.date);
    setPeriod(parsed.period);
    setNotes(order.notes ?? "");
    setDraftItems(order.items.map(toDraftItem));
  }, [order]);

  const editable = order?.status === "CRIADO";
  const editableFieldClass = editable ? "bg-white shadow-sm" : "bg-secondary/30";
  const sectionBgClass = editable ? "bg-secondary/20" : "bg-secondary/10";
  const tableRowBgClass = editable ? "bg-secondary/10" : "bg-secondary/5";

  const rowPrices = useMemo(() => {
    if (!order) return new Map<string, number | undefined>();

    return new Map(
      draftItems.map((item) => {
        const product = findProduct(activeProducts, item.productId);
        const currentLine = order.items.find((current) => current.externalId === item.externalId);
        const price = product
          ? resolveProductPriceQuote(product, item.unit, catalogUnits, item.quantity, item.cut).pricePerUnit
          : currentLine?.pricePerUnit;

        return [item.externalId, price] as const;
      }),
    );
  }, [activeProducts, catalogUnits, draftItems, order]);

  const total = useMemo(() => {
    let subtotal = 0;
    for (const item of draftItems) {
      const price = rowPrices.get(item.externalId);
      if (price === undefined) return null;
      subtotal += price * item.quantity;
    }
    return roundQuantity(subtotal, 2);
  }, [draftItems, rowPrices]);

  if (!order && !loading) return null;

  const addDraftItem = () => {
    setDraftItems((current) => [
      {
        externalId: crypto.randomUUID(),
        productId: "",
        unit: "",
        quantity: 1,
        cut: "",
        observation: "",
      },
      ...current,
    ]);
  };

  const updateDraftItem = (externalId: string, patch: Partial<DraftOrderItem>) => {
    setDraftItems((current) => current.map((item) => (item.externalId === externalId ? { ...item, ...patch } : item)));
  };

  const updateDraftProduct = (externalId: string, productId: string) => {
    const product = findProduct(activeProducts, productId);
    updateDraftItem(externalId, {
      productId,
      unit: normalizeUnit(product, product?.defaultUnit ?? ""),
      cut: normalizeCut(product, ""),
    });
  };

  const removeDraftItem = (externalId: string) => {
    setDraftItems((current) => current.filter((item) => item.externalId !== externalId));
  };

  const save = async () => {
    if (!order || !editable) return;
    if (!deliveryDate) {
      toast.error("Informe a data de entrega");
      return;
    }
    if (draftItems.length === 0) {
      toast.error("O pedido precisa ter ao menos um item");
      return;
    }
    if (draftItems.some((item) => !item.productId || !item.unit || item.quantity <= 0)) {
      toast.error("Revise os itens do pedido");
      return;
    }

    setIsSaving(true);
    try {
      const updated = await updateMyOrder(order.id, {
        deliveryWindow: formatDeliveryWindow(deliveryDate, period),
        notes: notes.trim() ? notes.trim() : null,
        items: draftItems.map((item) => ({
          externalId: item.externalId,
          productId: item.productId,
          unit: item.unit,
          quantity: roundQuantity(item.quantity, 3),
          cut: item.cut || null,
          observation: item.observation.trim() || null,
        })),
      });
      onOrderUpdated(updated);
      toast.success("Pedido atualizado");
      onOpenChange(false);
    } catch {
      toast.error("Nao foi possivel atualizar o pedido");
    } finally {
      setIsSaving(false);
    }
  };

  const cancelOrder = async () => {
    if (!order || !editable) return;
    setIsSaving(true);
    try {
      await deleteMyOrder(order.id);
      onOrderDeleted(order.id);
      toast.success("Pedido cancelado");
      onOpenChange(false);
    } catch {
      toast.error("Nao foi possivel cancelar o pedido");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={loading || !!order} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-6xl gap-0 overflow-hidden border-border bg-background p-0 shadow-2xl sm:rounded-xl">
        {loading && !order ? (
          <div className="flex min-h-[24rem] items-center justify-center px-6 py-10 text-sm text-muted-foreground">
            <div className="text-center">
              <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
              Carregando pedido...
            </div>
          </div>
        ) : order ? (
          <>
            <DialogHeader className="border-b border-border bg-surface-elevated px-6 py-4 pr-14 text-left">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <DialogTitle className="font-display text-xl">Editar pedido</DialogTitle>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <span>{new Date(order.createdAt).toLocaleDateString("pt-BR")}</span>
                    <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
                    <span>{ORDER_STATUS_LABELS[order.status]}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Total</div>
                  <div className="font-display text-2xl font-bold tabular-nums">{total !== null ? formatMoney(total) : "-"}</div>
                </div>
              </div>
            </DialogHeader>

            {!editable && order.status !== "CANCELADO" && (
              <div className="border-b border-amber-300/50 bg-amber-50 px-6 py-3 dark:bg-amber-950/30">
                <div className="flex items-start gap-2 text-sm">
                  <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
                  <div className="space-y-0.5">
                    <p className="font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
                      Pedido aprovado, não pode ser editado
                    </p>
                    <p className="text-xs text-amber-700/80 dark:text-amber-300/80">
                      Para alterações, entre em contato com o atendimento.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="max-h-[calc(92vh-9rem)] overflow-y-auto px-6 py-5">
              <div className={cn("mb-4 rounded-lg border border-border p-4", sectionBgClass)}>
                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_12rem_11rem]">
                  <div className="space-y-1.5">
                    <Label>Observações</Label>
                    <Textarea
                      value={notes}
                      onChange={(event) => setNotes(event.target.value)}
                      disabled={!editable}
                      rows={1}
                      className={cn("min-h-10 resize-none", editableFieldClass)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Data</Label>
                    <DatePicker
                      value={deliveryDate}
                      onChange={setDeliveryDate}
                      disabled={!editable}
                      className={editableFieldClass}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Período</Label>
                    {(() => {
                      const PRESETS = ["Manhã (06h - 10h)", "Tarde (13h - 17h)", "Madrugada (00h - 05h)"];
                      const CUSTOM = "__custom__";
                      const isCustom = !PRESETS.includes(period);
                      return (
                        <>
                          <Select
                            value={isCustom ? CUSTOM : period}
                            onValueChange={(value) => setPeriod(value === CUSTOM ? "" : value)}
                            disabled={!editable}
                          >
                            <SelectTrigger className={editableFieldClass}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Manhã (06h - 10h)">Manhã</SelectItem>
                              <SelectItem value="Tarde (13h - 17h)">Tarde</SelectItem>
                              <SelectItem value="Madrugada (00h - 05h)">Madrugada</SelectItem>
                              <SelectItem value={CUSTOM}>Personalizado…</SelectItem>
                            </SelectContent>
                          </Select>
                          {isCustom && (
                            <Input
                              className="mt-2"
                              value={period}
                              onChange={(e) => setPeriod(e.target.value)}
                              placeholder="ex.: 08h às 10h ou das 13h às 15h"
                              disabled={!editable}
                              autoFocus
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-sm text-muted-foreground">
                  {draftItems.length} {draftItems.length === 1 ? "item" : "itens"}
                </div>
                <Button type="button" variant="hero" onClick={addDraftItem} disabled={!editable}>
                  <Plus className="h-4 w-4" />
                  Adicionar
                </Button>
              </div>

              <div className={cn("overflow-hidden rounded-lg border border-border", sectionBgClass)}>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[960px] text-sm">
                    <thead className="border-b border-border bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="w-[32%] px-4 py-3 text-left font-semibold">Produto</th>
                        <th className="w-[14%] px-3 py-3 text-left font-semibold">Quantidade</th>
                        <th className="w-[20%] px-3 py-3 text-left font-semibold">Cut type</th>
                        <th className="w-[16%] px-3 py-3 text-left font-semibold">Unit</th>
                        <th className="w-[12%] px-3 py-3 text-right font-semibold">Preço</th>
                        <th className="w-[6%] px-3 py-3 text-right font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {draftItems.map((item) => {
                        const product = findProduct(activeProducts, item.productId);
                        const rowIsNew = !originalLineIds.has(item.externalId);
                        // Só oferece combinações disponíveis (match exato OU deriváveis do KG do corte).
                        // Produtos sem mapa de preço (legado/cotação) mantêm todas as opções.
                        const hasPricing = !!product?.priceByCutAndUnit
                          && Object.keys(product.priceByCutAndUnit).length > 0;
                        const units = hasPricing && product
                          ? offerableUnits(product, catalogUnits)
                          : productUnits(product);
                        const cuts = hasPricing && product
                          ? offerableCutsForUnit(product, item.unit, catalogUnits)
                          : productCuts(product);
                        const rowPrice = rowPrices.get(item.externalId);
                        const lineTotal = rowPrice === undefined ? null : rowPrice * item.quantity;

                        return (
                          <Fragment key={item.externalId}>
                          <tr className={cn("align-middle", tableRowBgClass)}>
                            <td className="px-4 py-3">
                              {rowIsNew && editable ? (
                                <ProductCombobox
                                  products={activeProducts}
                                  value={item.productId}
                                  onChange={(productId) => updateDraftProduct(item.externalId, productId)}
                                />
                              ) : (
                                <div className="flex min-w-0 items-center gap-3">
                                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-secondary text-xl">
                                    {product?.imageUrl ? (
                                      <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
                                    ) : (
                                      <span>{product?.emoji ?? "🌱"}</span>
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="truncate font-semibold uppercase">{product?.name ?? "Produto indisponível"}</div>
                                    <div className="truncate text-xs text-muted-foreground">{product?.category ?? item.productId}</div>
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-3">
                              <QuantityStepper
                                value={item.quantity}
                                onChange={(quantity) => updateDraftItem(item.externalId, { quantity })}
                                disabled={!editable}
                                className={cn("w-[8.5rem]", editable ? "bg-white" : "bg-secondary/30")}
                              />
                            </td>
                            <td className="px-3 py-3">
                              {cuts.length > 0 ? (
                                <Select
                                  value={item.cut || cuts[0]}
                                  onValueChange={(cut) => updateDraftItem(item.externalId, { cut })}
                                  disabled={!editable || !product}
                                >
                                  <SelectTrigger className={cn("h-11", editableFieldClass)}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {cuts.map((cut) => (
                                      <SelectItem key={cut} value={cut}>
                                        {cut}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  value={item.cut}
                                  onChange={(event) => updateDraftItem(item.externalId, { cut: event.target.value })}
                                  disabled={!editable || !product}
                                  className={cn("h-11", editableFieldClass)}
                                />
                              )}
                            </td>
                            <td className="px-3 py-3">
                              <Select
                                value={item.unit}
                                onValueChange={(unit) => updateDraftItem(item.externalId, { unit })}
                                disabled={!editable || !product}
                              >
                                <SelectTrigger className={cn("h-11", editableFieldClass)}>
                                  <SelectValue placeholder="Unit" />
                                </SelectTrigger>
                                <SelectContent>
                                  {units.map((unit) => (
                                    <SelectItem key={unit} value={unit}>
                                      {unit}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-3 py-3 text-right font-display font-semibold tabular-nums">
                              {lineTotal === null ? "-" : formatMoney(lineTotal)}
                            </td>
                            <td className="px-3 py-3 text-right">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground hover:text-destructive"
                                onClick={() => removeDraftItem(item.externalId)}
                                disabled={!editable}
                                aria-label="Remover linha"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                          <tr className={cn(tableRowBgClass)}>
                            <td colSpan={6} className="px-4 pb-3">
                              <Input
                                value={item.observation}
                                onChange={(event) => updateDraftItem(item.externalId, { observation: event.target.value })}
                                disabled={!editable}
                                placeholder="Observação do item (opcional)"
                                className={cn("h-9 text-sm", editableFieldClass)}
                              />
                            </td>
                          </tr>
                          </Fragment>
                        );
                      })}
                      {draftItems.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">
                            Nenhum item no pedido.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface-elevated px-6 py-4">
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={() => setIsCancelDialogOpen(true)}
                disabled={!editable || isSaving}
              >
                Cancelar pedido
              </Button>
              <div className="flex gap-2">
                <Button variant="hero" onClick={() => void save()} disabled={!editable || isSaving}>
                  Salvar alterações
                </Button>
              </div>
            </div>
          </>
        ) : null}

        <AlertDialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancelar pedido?</AlertDialogTitle>
              <AlertDialogDescription>
                Voce pode criar um novo pedido depois. Esta acao remove este pedido do seu historico.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSaving}>Voltar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => void cancelOrder()}
                disabled={isSaving}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Cancelar pedido
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

function ProductCombobox({
  products,
  value,
  onChange,
}: {
  products: Product[];
  value: string;
  onChange: (productId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = findProduct(products, value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-11 w-full justify-between bg-white px-3 font-normal shadow-sm"
        >
          <span className="min-w-0 truncate">
            {selected ? `${selected.emoji ?? ""} ${selected.name}`.trim() : "Selecionar produto"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar produto..." />
          <CommandList>
            <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
            <CommandGroup>
              {products.map((product) => (
                <CommandItem
                  key={product.id}
                  value={`${product.name} ${product.category} ${product.emoji ?? ""}`}
                  onSelect={() => {
                    onChange(product.id);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === product.id ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">
                    {product.emoji ? `${product.emoji} ` : ""}
                    {product.name}
                  </span>
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">{product.category}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
